"use client";

function HalloweenWorkshopPage(props: { yScroll?: number }) {
  return (
    <>
      <div className="pt-24 pb-6 px-2 md:px-[10%] flex items-center justify-center emoji-background">
        <div className="container flex flex-col justify-center gap-2 relative">
          <h1 className="text-2xl text-black font-bold mb-2">
            Halloween 3D Modeling & Printing Workshop for Kids
          </h1>
          <div className="text-lg mb-4">
            Welcome to Our Spooky STEM Adventure!
          </div>
          <div className="text-base mb-4">
            Join us at our studio in Causeway Bay, Hong Kong, for a fun,
            hands-on Halloween-themed workshop! Kids will learn the basics of 3D
            modeling, watch their designs come to life with our Bambu Lab
            multi-color PLA 3D printer, and add colorful flair with acrylic
            paints. Perfect for young creators aged 8-12 to celebrate Halloween
            while building tech and art skills. Led by our experienced
            architectural designer tutor, no prior experience needed – we'll
            guide you every step of the way!
          </div>
        </div>
      </div>

      <div className="px-2 md:px-[10%] mb-6">
        <div className="bg-primary text-white text-center py-4 px-2 md:px-[10%] mb-6">
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLScdD4z2-zZKHWOQR7MOcwOTbV7FHmdsXWO34xa7w_VXsn2IKA/viewform?usp=publish-editor"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-xl font-semibold"
          >
            Click here to register!
          </a>
        </div>
      </div>

      {/* Poster Image */}
      <div className="px-2 md:px-[10%] mb-6">
        <img
          src="/images/stem/poster.png"
          alt="Halloween 3D Modeling & Printing Workshop for Kids Poster"
          className="w-full md:max-h-[500px] lg:max-h-[700px] object-contain"
        />
      </div>

      <div className="px-2 md:px-[10%] mb-8">
        <h2 className="text-xl font-semibold mb-4 text-black">
          Workshop Details
        </h2>
        <div className="flex flex-col gap-4 mb-6">
          <div>
            <strong>Theme:</strong> Design and create your own Halloween pumpkin
            (or ghost/jack-o'-lantern) using 3D tools.
          </div>
          <div>
            <strong>Format:</strong> Two interactive lessons (2 hours each).
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>
                <strong>Lesson A: 3D Modeling</strong> – Learn to design your
                spooky creation using 3D software like Blender. Export your
                model ready for printing.
              </li>
              <li>
                <strong>Between Lessons:</strong> We'll 3D print your designs at
                the studio using our Bambu Lab multi-color printer for vibrant
                results right out of the printer.
              </li>
              <li>
                <strong>Lesson B: Painting & Finishing</strong> – Sand, prime,
                and paint your multi-color printed model with acrylic colors for
                extra details and a personalized Halloween finish.
              </li>
            </ul>
          </div>
          <div>
            <strong>Available Sessions</strong> (starting from today, 25th
            October 2025 – register for your preferred batch):
            <div className="mt-2 space-y-2">
              <div>
                <strong>Lesson A Time Slots</strong>
                <ul className="list-disc ml-6 space-y-1">
                  <li>A1: Sunday (October 26): 10:00 AM – 12:00 NN</li>
                  <li>A2: Sunday (October 26): 1:00 PM - 3:00 PM</li>
                  <li>A3: Sunday (October 26): 3:15 PM - 5:15 PM</li>
                  <li>A4: Sunday (October 26): 6:00 PM - 8:00 PM</li>
                  <li>A5: Monday (October 27): 5:00 PM - 7:00 PM</li>
                  <li>A6: Tuesday (October 28): 5:00 PM - 7:00 PM</li>
                </ul>
              </div>
              <div>
                <strong>Lesson B Time Slots</strong>
                <ul className="list-disc ml-6 space-y-1">
                  <li>B1: Wednesday (October 29): 10:00 AM – 12:00 NN</li>
                  <li>B2: Wednesday (October 29): 1:00 PM - 3:00 PM</li>
                  <li>B3: Wednesday (October 29): 3:15 PM - 5:15 PM</li>
                  <li>B4: Wednesday (October 29): 6:00 PM - 8:00 PM</li>
                  <li>B5: Thursday (October 30): 5:00 PM - 7:00 PM</li>
                  <li>B6: Halloween Friday (October 31): 5:00 PM - 7:00 PM</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <strong>Location:</strong> Maker Space, 2 Cannon St, Causeway Bay,
              Hong Kong (right next to SOGO depart)
            </div>
            <div>
              <strong>Group Size:</strong> 5-10 kids per class for personalized
              attention.
            </div>
            <div>
              <strong>Price:</strong> HKD 250 per child (includes all materials:
              multi-color PLA filament, paints, brushes, and protective gear).
            </div>
            <div>
              <strong>What You'll Take Home:</strong> Your custom multi-color 3D
              printed and painted Halloween decoration!
            </div>
          </div>
        </div>

        <h2 className="text-xl font-semibold mb-4 text-black">Why Join?</h2>
        <ul className="list-disc ml-6 space-y-1 mb-8">
          <li>
            Hands-on learning: Combine STEM (3D design & printing) with creative
            art, using advanced multi-color printing technology from Bambu Lab.
          </li>
          <li>
            Safe & Fun: Non-toxic PLA materials, supervised activities, and
            Halloween excitement.
          </li>
          <li>Inspired by our previous pumpkin prints – see examples below!</li>
        </ul>

        <div className="mb-8">
          <div className="text-center text-gray-500">
            Examples of previous pumpkin prints
            <div className="space-y-2">
              <img
                src="/images/stem/previous-1.jpg"
                alt="Previous Pumpkin prints - 1"
                className="w-full md:max-h-96 object-contain"
              />
              <img
                src="/images/stem/previous-2.jpg"
                alt="Previous Pumpkin prints - 2"
                className="w-full md:max-h-96 object-contain"
              />
              <img
                src="/images/stem/previous-3.jpg"
                alt="Previous Pumpkin prints - 3"
                className="w-full md:max-h-96 object-contain"
              />
              <img
                src="/images/stem/previous-4.png"
                alt="Previous Pumpkin prints - 4"
                className="w-full md:max-h-96 object-contain"
              />
            </div>
          </div>
        </div>

        <h2 className="text-xl font-semibold mb-4 text-black">Requirements</h2>
        <ul className="list-disc ml-6 space-y-1 mb-8">
          <li>
            Bring your curiosity! We'll provide computers/tablets if needed.
          </li>
          <li>Parents/guardians welcome to observe.</li>
          <li>
            Safety first: Good ventilation and protective aprons included.
          </li>
        </ul>

        <h2 className="text-xl font-semibold mb-4 text-black">
          How to Register
        </h2>
        <div className="mb-12">
          <div className="mb-2">
            <div className="py-4 px-2 bg-primary text-center">
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLScdD4z2-zZKHWOQR7MOcwOTbV7FHmdsXWO34xa7w_VXsn2IKA/viewform?usp=publish-editor"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white underline font-semibold text-xl"
              >
                Registration form
              </a>
            </div>
          </div>
          <div className="mb-2">OR,</div>
          <div className="mb-2">
            Email:{" "}
            <a
              href="mailto:stem@tonsofminds.com"
              className="text-primary underline"
            >
              stem@tonsofminds.com
            </a>
          </div>
          <div className="mb-2">
            Call:{" "}
            <span className="text-primary underline">(852) 6761 0002</span>
          </div>
          <div className="mb-2">
            WeChat: <span className="text-primary underline">jw6666555</span>
          </div>
          <div>
            Provide child's name, age, preferred session, and any special needs.
            Get ready for a ghoulishly good time – see you soon! 🎃
          </div>
        </div>
      </div>

      <div className="absolute left-4 bottom-4 bg-white rounded-full p-3 border">
        <a
          href="https://docs.google.com/forms/d/e/1FAIpQLScdD4z2-zZKHWOQR7MOcwOTbV7FHmdsXWO34xa7w_VXsn2IKA/viewform?usp=publish-editor"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white underline font-semibold text-xl"
        >
          <img
            src="/images/stem/pumpkin.svg"
            alt="Pumpkin"
            className="size-12 md:size-16"
          />
        </a>
      </div>
    </>
  );
}

export default HalloweenWorkshopPage;
